import classNames from 'classnames';
import type { CSSProperties } from 'react';
import { lazy, Suspense, useRef, useState } from 'react';
import {
    getDocumentUrlFromAgUrl,
    getRenderingUrl,
    getRenderingUrlDocument,
    getRenderingUrlWithPhotoScene,
} from '../../../../api';
import { projectionMapper, useCancelableEffect } from '../../../../tools';
import { ErrorIndicator, LoadingIndicator, PanelSwitcher, PhotoSceneToggle } from '../components';
import classes from '../PreviewCard.module.css';
import type { PreviewRendererProps } from '../types';
import { useLoadCardData } from '../useLoadCardData';
import { dimensionsToString } from './helpers';

const DebugInfo = lazy(() => import('../components/DebugInfo'));

const clampPercent = (value: number): number => Math.max(0, Math.min(100, value));

const parseDimension = (value: string | undefined): number | null => {
    if (!value) {
        return null;
    }

    const parsed = Number.parseFloat(value);

    return Number.isFinite(parsed) ? parsed : null;
};

const getContentAreaStyle = (
    width: number,
    height: number,
    contentAreaOverlay: { x: number; y: number; width: number; height: number },
): CSSProperties => {
    const left = clampPercent((contentAreaOverlay.x / width) * 100);
    const top = clampPercent((contentAreaOverlay.y / height) * 100);
    const overlayWidth = clampPercent((contentAreaOverlay.width / width) * 100);
    const overlayHeight = clampPercent((contentAreaOverlay.height / height) * 100);

    return {
        left: `${left}%`,
        top: `${top}%`,
        width: `${overlayWidth}%`,
        height: `${overlayHeight}%`,
    };
};

export const DocumentUrlPreviewRenderer = ({ loader, onAgsMetadataLoaded }: PreviewRendererProps<'documentUrl'>) => {
    const [showPhotoScene, setShowPhotoScene] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');
    const previousPreviewUrlRef = useRef<string>('');
    const previousSourceDocumentUrlRef = useRef<string | undefined>(undefined);

    const {
        data: previewData,
        isLoading,
        loadingLabel,
        hasError,
        errorMessage,
        panels,
        panelIndex,
        onPreviewLoading,
        onPreviewLoaded,
        onPreviewError,
        onPanelIndexChange,
    } = useLoadCardData(loader, onAgsMetadataLoaded);

    useCancelableEffect(
        (signal) => {
            (async () => {
                if (!previewData || !previewData.documentUrl) {
                    return;
                }
                if (previousSourceDocumentUrlRef.current !== previewData.documentUrl) {
                    previousSourceDocumentUrlRef.current = previewData.documentUrl;
                    previousPreviewUrlRef.current = '';
                }
                onPreviewLoading();

                const { documentUrl, backgroundColor, photoSceneUrl, projection, surfaceUrl } = previewData;

                let newPreviewUrl = '';

                const transientDocumentUrl = await getDocumentUrlFromAgUrl(documentUrl, signal);

                if (photoSceneUrl && showPhotoScene) {
                    newPreviewUrl = getRenderingUrlWithPhotoScene(transientDocumentUrl, photoSceneUrl);
                } else {
                    newPreviewUrl = surfaceUrl
                        ? getRenderingUrl(
                              transientDocumentUrl,
                              panelIndex,
                              surfaceUrl,
                              projectionMapper(projection, 1),
                              backgroundColor,
                          )
                        : getRenderingUrlDocument(transientDocumentUrl, panelIndex, backgroundColor);
                }

                if (newPreviewUrl !== previousPreviewUrlRef.current) {
                    previousPreviewUrlRef.current = newPreviewUrl;
                    setPreviewUrl(newPreviewUrl);
                } else {
                    onPreviewLoaded();
                }
            })();
        },
        [previewData, panelIndex, showPhotoScene, onPreviewLoading, onPreviewLoaded],
    );

    const { documentUrl, debug, photoSceneUrl, surfaceUrl, contentAreaOverlay } = previewData ?? {};
    const panel = panels[panelIndex];
    const panelWidth = parseDimension(panel?.width);
    const panelHeight = parseDimension(panel?.height);
    const shouldRenderContentAreaOverlay = !!(
        contentAreaOverlay &&
        panelWidth &&
        panelHeight &&
        panelWidth > 0 &&
        panelHeight > 0
    );

    const showDebugInfo = !!(photoSceneUrl && debug && documentUrl && surfaceUrl);

    return (
        <>
            {photoSceneUrl && <PhotoSceneToggle value={showPhotoScene} onChange={setShowPhotoScene} />}

            {isLoading && <LoadingIndicator label={loadingLabel} />}

            {hasError && <ErrorIndicator message={errorMessage} errorUrl={previewUrl} />}

            {!hasError && documentUrl && previewUrl && (
                <div className={classNames(classes.preview, { [classes.hidden]: isLoading })}>
                    <a href={documentUrl} target="_blank" rel="noreferrer" className={classes.previewLink}>
                        <div className={classes.previewImageWrapper}>
                            <img
                                key={`${documentUrl}|${previewUrl}`}
                                src={previewUrl}
                                alt=""
                                onLoad={onPreviewLoaded}
                                onError={onPreviewError}
                            />

                            {shouldRenderContentAreaOverlay && contentAreaOverlay && panelWidth && panelHeight && (
                                <div
                                    className={classes.contentAreaOverlay}
                                    style={getContentAreaStyle(panelWidth, panelHeight, contentAreaOverlay)}
                                />
                            )}
                        </div>
                    </a>
                    <span>{dimensionsToString(panels, panelIndex)}</span>
                </div>
            )}

            {!hasError && showDebugInfo && (
                <Suspense fallback="Loading...">
                    <DebugInfo documentUrl={documentUrl} surfaceUrl={surfaceUrl} />
                </Suspense>
            )}

            <PanelSwitcher panelCount={panels.length} panelIndex={panelIndex} onChange={onPanelIndexChange} />
        </>
    );
};
